import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CaptchaVerification from "./CaptchaVerification";
import AuthCard from "./auth/AuthCard";
import Notice from "./ui/Notice";
import { Button, Field, Input } from "../designSystem";
import { validateEmail, translateAuthError } from "../utils";
import { getFullApiUrl, podcastId } from "../config";

// Get or generate device ID for tracking
const getDeviceId = () => {
  let deviceId = localStorage.getItem("deviceId");
  if (!deviceId) {
    deviceId = `web-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("deviceId", deviceId);
  }
  return deviceId;
};

// 注册改为邮箱验证制：先向邮箱发送注册链接，用户点击邮件中的链接
// （/complete_signup）设置密码后才创建账号，避免填错邮箱后付费导致无法登录。
export default function SignupPage() {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [captchaConfig, setCaptchaConfig] = useState(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    fetchCaptchaConfig();
  }, []);

  // Timer effect for resend cooldown
  useEffect(() => {
    if (remainingTime > 0) {
      timerRef.current = setTimeout(() => {
        setRemainingTime(remainingTime - 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [remainingTime]);

  const fetchCaptchaConfig = async () => {
    try {
      const response = await axios.get(
        getFullApiUrl("/api/v1/auth/captcha-config")
      );
      setCaptchaConfig(response.data);
    } catch (err) {
      console.error("Failed to fetch CAPTCHA config:", err);
    }
  };

  const sendSignupEmail = async (captchaParam) => {
    setIsLoading(true);
    setMessage("");
    try {
      const payload = {
        email: email,
        podcast_id: podcastId,
        device_id: getDeviceId(),
      };
      if (captchaParam) {
        payload.captcha_verify_param = captchaParam;
      }

      const response = await axios.post(
        getFullApiUrl("/api/v1/auth/send-login-code"),
        payload
      );

      if (response.status === 200) {
        setSent(true);
        setRemainingTime(60);
      }
    } catch (err) {
      console.error("Failed to send signup email:", err);

      const translated = translateAuthError(
        err.response?.data?.error || err.message,
        err
      );

      if (translated.captchaRequired || err.response?.data?.captcha_required) {
        if (
          captchaConfig?.enabled &&
          captchaConfig?.app_key &&
          captchaConfig?.scene_id
        ) {
          setShowCaptcha(true);
        } else {
          setMessage("安全验证不可用，请稍后重试");
        }
      } else if (translated.switchToPassword) {
        // 注册场景没有密码可用，覆盖“请使用密码登录”的提示
        setMessage("今日邮件发送次数已达上限，请明天再试");
      } else {
        setMessage(translated.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaptchaSuccess = async (verifyParam) => {
    setShowCaptcha(false);
    await sendSignupEmail(verifyParam);
  };

  const handleCaptchaError = () => {
    setShowCaptcha(false);
    setMessage("安全验证失败，请重试");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateEmail(email)) {
      setMessage("请输入有效的邮箱地址");
      return;
    }
    await sendSignupEmail();
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  if (sent) {
    return (
      <AuthCard title="查收邮件" subtitle={`我们已向 ${email} 发送了一封邮件。`}>
        <div style={{ display: "grid", gap: 16 }}>
          <Notice variant="success">
            请点击邮件中的“完成注册”链接设置密码，链接 15 分钟内有效。如未收到，请检查垃圾邮件。
          </Notice>
          <Notice variant="info">
            如果该邮箱已注册过，邮件中会是登录验证码，请前往登录页输入验证码登录。
          </Notice>
          <Notice variant="error">{message}</Notice>
          <Button size="lg" fullWidth onClick={() => navigate("/login")}>
            前往登录
          </Button>
          <div className="lt-auth-links" style={{ justifyContent: "center" }}>
            {remainingTime > 0 ? (
              <span
                style={{
                  fontSize: "var(--text-sm)",
                  color: "var(--text-3)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                重新发送 ({formatTime(remainingTime)})
              </span>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                loading={isLoading}
                onClick={() => sendSignupEmail()}
              >
                重新发送邮件
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setSent(false);
                setMessage("");
                setRemainingTime(0);
              }}
            >
              更换邮箱
            </Button>
          </div>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="注册"
      subtitle="输入邮箱，我们将发送注册链接，验证邮箱后即可完成注册。"
    >
      <form onSubmit={handleSubmit} noValidate style={{ display: "grid", gap: 16 }}>
        <Field label="邮箱" htmlFor="email">
          <Input
            id="email"
            name="email"
            type="email"
            icon="mail"
            placeholder="you@example.com"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={showCaptcha}
          />
        </Field>

        {showCaptcha && captchaConfig?.enabled && (
          <CaptchaVerification
            appKey={captchaConfig.app_key}
            sceneId={captchaConfig.scene_id}
            onSuccess={handleCaptchaSuccess}
            onError={handleCaptchaError}
            onCancel={() => {
              setShowCaptcha(false);
            }}
            embedded={true}
          />
        )}

        <Notice variant="error">{message}</Notice>

        {!showCaptcha && (
          <Button
            type="submit"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading || !validateEmail(email)}
          >
            发送注册邮件
          </Button>
        )}

        <div className="lt-auth-links" style={{ justifyContent: "center" }}>
          <span
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--text-3)",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            已有账号？
            <Button type="button" variant="ghost" size="sm" onClick={() => navigate("/login")}>
              登录
            </Button>
          </span>
        </div>
      </form>
    </AuthCard>
  );
}
